import React, { useState } from 'react';
import { Loader2, ShieldCheck, ChevronDown, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { SignaturePad } from './SignaturePad';
import { PhotoPicker } from './PhotoPicker';

const REQUIRED = [
  ['full_name', 'Full name (as on NIDA)', 'Jina kamili (kama kwenye NIDA)', 'text'],
  ['phone', 'Mobile number', 'Namba ya simu', 'tel'],
  ['nida', 'NIDA number (20 digits)', 'Namba ya NIDA (tarakimu 20)', 'text'],
  ['nominee_name', 'Next of kin full name', 'Jina kamili la ndugu wa karibu', 'text'],
  ['nominee_phone', 'Next of kin phone', 'Simu ya ndugu wa karibu', 'tel'],
];
const OPTIONAL = [
  ['email', 'Email address', 'Barua pepe', 'email'],
  ['dob', 'Date of birth', 'Tarehe ya kuzaliwa', 'date'],
  ['region', 'Region', 'Mkoa', 'text'],
  ['address', 'Current address', 'Anuani ya sasa', 'text'],
  ['nominee_relationship', 'Next of kin relationship', 'Uhusiano na ndugu wa karibu', 'text'],
  ['bank_name', 'Bank', 'Benki', 'text'],
  ['bank_branch', 'Bank branch', 'Tawi la benki', 'text'],
  ['bank_account', 'Bank account number', 'Namba ya akaunti ya benki', 'text'],
];
const CHOICES = [
  ['gender', 'Gender', 'Jinsia', [['male', 'Male', 'Mwanaume'], ['female', 'Female', 'Mwanamke']]],
  ['occupation', 'Occupation', 'Kazi', [['agriculture', 'Agriculture', 'Mkulima'], ['employed', 'Employed', 'Mwajiriwa'],
    ['business', 'Business', 'Mfanyabiashara'], ['retired', 'Retired', 'Mstaafu'], ['other', 'Other', 'Mengineyo']]],
  ['source_of_funds', 'Source of funds', 'Chanzo cha fedha', [['salary', 'Salary', 'Mshahara'], ['business', 'Business', 'Biashara'],
    ['other', 'Other sources', 'Vyanzo vinginevyo']]],
];

export const ApplicationForm = ({ initial, onSubmit, onPreview, hasPhoto }) => {
  const { t, language } = useLanguage();
  const [form, setForm] = useState(initial);
  const [consent, setConsent] = useState(false);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [signature, setSignature] = useState(null);
  const [photo, setPhoto] = useState(null);
  const sw = language === 'sw';
  const ready = REQUIRED.every(([k]) => (form[k] || '').trim()) && consent && !!signature;

  const field = ([k, en, swl, type], req) => (
    <label key={k} className="block text-xs text-muted-foreground">
      {sw ? swl : en}{req && <span className="text-red-500"> *</span>}
      <Input type={type} value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="mt-1" data-testid={`utt-app-${k}`} required={req} />
    </label>
  );

  const choice = ([k, en, swl, opts]) => (
    <label key={k} className="block text-xs text-muted-foreground">
      {sw ? swl : en}
      <select value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`utt-app-${k}`}
        className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground">
        <option value="">{sw ? 'Chagua' : 'Select'}</option>
        {opts.map(([v, oen, osw]) => <option key={v} value={v}>{sw ? osw : oen}</option>)}
      </select>
    </label>
  );

  const payload = () => {
    const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== '' && v != null && !String(v).startsWith('••••')));
    return { ...body, signature, consent, ...(photo ? { photo } : {}) };
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onSubmit(payload());
    setBusy(false);
  };
  const preview = async () => {
    setPreviewing(true);
    await onPreview(payload());
    setPreviewing(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="utt-application-form">
      <p className="text-sm text-muted-foreground">
        {t('One page, once. Wekeza-Smart prepares the official paperwork with your signature and sends it to UTT AMIS (through CORE Securities) for you.',
           'Ukurasa mmoja, mara moja. Wekeza-Smart inaandaa nyaraka rasmi pamoja na saini yako na kuzituma UTT AMIS (kupitia CORE Securities) kwa niaba yako.')}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">{REQUIRED.map((f) => field(f, true))}</div>
      <button type="button" onClick={() => setMore(!more)} data-testid="utt-app-more-toggle"
        className="flex items-center gap-1 text-sm font-medium text-primary">
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${more ? 'rotate-180' : ''}`} />
        {t('More details (optional): gender, occupation, region, bank and more', 'Taarifa zaidi (si lazima): jinsia, kazi, mkoa, benki na zaidi')}
      </button>
      {more && <div className="grid gap-3 sm:grid-cols-2">{CHOICES.map(choice)}{OPTIONAL.map((f) => field(f, false))}</div>}
      <PhotoPicker onChange={setPhoto} hasExisting={hasPhoto} />
      <SignaturePad onChange={setSignature} />
      <label className="flex items-start gap-2 rounded-xl bg-muted/50 p-3 text-xs">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="utt-app-consent" />
        <span>{t('I authorise Wekeza-Smart and CORE Securities to share these details and my signature with UTT AMIS to open my investor account.',
                 'Ninaruhusu Wekeza-Smart na CORE Securities kushiriki taarifa hizi na saini yangu na UTT AMIS ili kunifungulia akaunti ya mwekezaji.')}</span>
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button type="button" variant="outline" onClick={preview} disabled={previewing || !REQUIRED.every(([k]) => (form[k] || '').trim())} data-testid="utt-app-preview">
          {previewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
          {t('Preview my UTT form', 'Hakiki fomu yangu ya UTT')}
        </Button>
        <Button type="submit" disabled={!ready || busy} data-testid="utt-app-submit">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
          {t('Send my application to UTT AMIS', 'Tuma ombi langu UTT AMIS')}
        </Button>
      </div>
    </form>
  );
};
